import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '../../i18n/hooks';
import { Button } from '../../ui';
import './EducationalContent.css';

/**
 * EducationalContent - Educational sections about surebet betting, arbitrage, and app benefits
 * 
 * Features:
 * - "What is Surebet" explanation section
 * - "How to avoid bookmaker limitations" content
 * - "Why this app is unique" section
 * - Interactive examples and scenarios
 * - Expandable content sections
 * - Visual aids and infographics
 */
const EducationalContent = ({ onRegisterClick, className = '' }) => {
  const { t, language } = useTranslation();
  const [expandedSections, setExpandedSections] = useState({});
  const currency = language === 'bg' ? 'евро' : 'euro';

  /**
   * Toggle section expansion
   */
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  /**
   * Check if section is expanded
   */
  const isExpanded = (sectionId) => expandedSections[sectionId] || false;

  return (
    <section className={`educational-content ${className}`}>
      <div className="educational-content__container">
        {/* Why This App is Unique Section */}
        <div className="educational-section">
          <div className="educational-section__header">
            <h2 className="educational-section__title">
              {t('landing.educational.whyUnique.title')}
            </h2>
            <button
              className={`educational-section__toggle ${isExpanded('whyUnique') ? 'expanded' : ''}`}
              onClick={() => toggleSection('whyUnique')}
              aria-expanded={isExpanded('whyUnique')}
              aria-label={isExpanded('whyUnique') ? t('landing.educational.collapse') : t('landing.educational.expand')}
            >
              <svg
                className="educational-section__toggle-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {isExpanded('whyUnique') && (
            <div className="educational-section__content">
              <div className="educational-section__intro">
                <p className="educational-section__intro-text">
                  {t('landing.educational.whyUnique.intro')}
                </p>
              </div>

              <div className="educational-section__features">
                <div className="educational-feature">
                  <div className="educational-feature__icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div className="educational-feature__content">
                    <h3 className="educational-feature__title">
                      {t('landing.educational.whyUnique.feature1.title')}
                    </h3>
                    <p className="educational-feature__description">
                      {t('landing.educational.whyUnique.feature1.description')}
                    </p>
                  </div>
                </div>

                <div className="educational-feature">
                  <div className="educational-feature__icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div className="educational-feature__content">
                    <h3 className="educational-feature__title">
                      {t('landing.educational.whyUnique.feature2.title')}
                    </h3>
                    <p className="educational-feature__description">
                      {t('landing.educational.whyUnique.feature2.description')}
                    </p>
                  </div>
                </div>

                <div className="educational-feature">
                  <div className="educational-feature__icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="educational-feature__content">
                    <h3 className="educational-feature__title">
                      {t('landing.educational.whyUnique.feature3.title')}
                    </h3>
                    <p className="educational-feature__description">
                      {t('landing.educational.whyUnique.feature3.description')}
                    </p>
                  </div>
                </div>

                <div className="educational-feature">
                  <div className="educational-feature__icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div className="educational-feature__content">
                    <h3 className="educational-feature__title">
                      {t('landing.educational.whyUnique.feature4.title')}
                    </h3>
                    <p className="educational-feature__description">
                      {t('landing.educational.whyUnique.feature4.description')}
                    </p>
                  </div>
                </div>

                <div className="educational-feature">
                  <div className="educational-feature__icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <div className="educational-feature__content">
                    <h3 className="educational-feature__title">
                      {t('landing.educational.whyUnique.feature5.title')}
                    </h3>
                    <p className="educational-feature__description">
                      {t('landing.educational.whyUnique.feature5.description')}
                    </p>
                  </div>
                </div>

                <div className="educational-feature">
                  <div className="educational-feature__icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <div className="educational-feature__content">
                    <h3 className="educational-feature__title">
                      {t('landing.educational.whyUnique.feature6.title')}
                    </h3>
                    <p className="educational-feature__description">
                      {t('landing.educational.whyUnique.feature6.description')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="educational-section__comparison">
                <h3 className="educational-section__comparison-title">
                  {t('landing.educational.whyUnique.comparison.title')}
                </h3>
                <div className="educational-comparison">
                  <div className="educational-comparison__other">
                    <div className="educational-comparison__header">
                      {t('landing.educational.whyUnique.comparison.other.title')}
                    </div>
                    <ul className="educational-comparison__list">
                      <li className="educational-comparison__item educational-comparison__item--negative">
                        {t('landing.educational.whyUnique.comparison.other.point1')}
                      </li>
                      <li className="educational-comparison__item educational-comparison__item--negative">
                        {t('landing.educational.whyUnique.comparison.other.point2')}
                      </li>
                      <li className="educational-comparison__item educational-comparison__item--negative">
                        {t('landing.educational.whyUnique.comparison.other.point3')}
                      </li>
                    </ul>
                  </div>

                  <div className="educational-comparison__our">
                    <div className="educational-comparison__header educational-comparison__header--highlight">
                      {t('landing.educational.whyUnique.comparison.our.title')}
                    </div>
                    <ul className="educational-comparison__list">
                      <li className="educational-comparison__item educational-comparison__item--positive">
                        {t('landing.educational.whyUnique.comparison.our.point1')}
                      </li>
                      <li className="educational-comparison__item educational-comparison__item--positive">
                        {t('landing.educational.whyUnique.comparison.our.point2')}
                      </li>
                      <li className="educational-comparison__item educational-comparison__item--positive">
                        {t('landing.educational.whyUnique.comparison.our.point3')}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* What is Surebet Section */}
        <div id="what-is-surebet" className="educational-section">
          <div className="educational-section__header">
            <h2 className="educational-section__title">
              {t('landing.educational.whatIsSurebet.title')}
            </h2>
            <button
              className={`educational-section__toggle ${isExpanded('whatIsSurebet') ? 'expanded' : ''}`}
              onClick={() => toggleSection('whatIsSurebet')}
              aria-expanded={isExpanded('whatIsSurebet')}
              aria-label={isExpanded('whatIsSurebet') ? t('landing.educational.collapse') : t('landing.educational.expand')}
            >
              <svg
                className="educational-section__toggle-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          
          {isExpanded('whatIsSurebet') && (
            <div className="educational-section__content">
              <div className="educational-section__intro">
                <p className="educational-section__intro-text">
                  {t('landing.educational.whatIsSurebet.intro')}
                </p>
              </div>

              <div className="educational-section__visual">
                <div className="educational-visual">
                  <div className="educational-visual__title">
                    {t('landing.educational.whatIsSurebet.visual.title')}
                  </div>
                  <div className="educational-visual__example">
                    <div className="educational-visual__bookmaker">
                      <div className="educational-visual__bookmaker-name">
                        {t('landing.educational.whatIsSurebet.visual.bookmaker1')}
                      </div>
                      <div className="educational-visual__odds">
                        <span className="educational-visual__outcome">{t('landing.educational.whatIsSurebet.visual.outcome1')}</span>
                        <span className="educational-visual__odds-value">2.10</span>
                      </div>
                    </div>
                    <div className="educational-visual__vs">VS</div>
                    <div className="educational-visual__bookmaker">
                      <div className="educational-visual__bookmaker-name">
                        {t('landing.educational.whatIsSurebet.visual.bookmaker2')}
                      </div>
                      <div className="educational-visual__odds">
                        <span className="educational-visual__outcome">{t('landing.educational.whatIsSurebet.visual.outcome2')}</span>
                        <span className="educational-visual__odds-value">2.15</span>
                      </div>
                    </div>
                    <div className="educational-visual__profit">
                      <div className="educational-visual__profit-label">
                        {t('landing.educational.whatIsSurebet.visual.profit')}
                      </div>
                      <div className="educational-visual__profit-value">+2.5%</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="educational-section__points">
                <div className="educational-point">
                  <div className="educational-point__icon"></div>
                  <div className="educational-point__content">
                    <h3 className="educational-point__title">
                      {t('landing.educational.whatIsSurebet.point1.title')}
                    </h3>
                    <p className="educational-point__description">
                      {t('landing.educational.whatIsSurebet.point1.description')}
                    </p>
                  </div>
                </div>

                <div className="educational-point">
                  <div className="educational-point__icon"></div>
                  <div className="educational-point__content">
                    <h3 className="educational-point__title">
                      {t('landing.educational.whatIsSurebet.point2.title')}
                    </h3>
                    <p className="educational-point__description">
                      {t('landing.educational.whatIsSurebet.point2.description')}
                    </p>
                  </div>
                </div>

                <div className="educational-point">
                  <div className="educational-point__icon"></div>
                  <div className="educational-point__content">
                    <h3 className="educational-point__title">
                      {t('landing.educational.whatIsSurebet.point3.title')}
                    </h3>
                    <p className="educational-point__description">
                      {t('landing.educational.whatIsSurebet.point3.description')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="educational-section__example">
                <h3 className="educational-section__example-title">
                  {t('landing.educational.whatIsSurebet.example.title')}
                </h3>
                <div className="educational-example">
                  <div className="educational-example__scenario">
                    <p className="educational-example__text">
                      {t('landing.educational.whatIsSurebet.example.scenario')}
                    </p>
                  </div>
                  <div className="educational-example__calculation">
                    <div className="educational-example__step">
                      <span className="educational-example__step-number">1</span>
                      <span className="educational-example__step-text">
                        {t('landing.educational.whatIsSurebet.example.step1')}
                      </span>
                    </div>
                    <div className="educational-example__step">
                      <span className="educational-example__step-number">2</span>
                      <span className="educational-example__step-text">
                        {t('landing.educational.whatIsSurebet.example.step2')}
                      </span>
                    </div>
                    <div className="educational-example__step">
                      <span className="educational-example__step-number">3</span>
                      <span className="educational-example__step-text">
                        {t('landing.educational.whatIsSurebet.example.step3')}
                      </span>
                    </div>
                    <div className="educational-example__result">
                      <span className="educational-example__result-label">
                        {t('landing.educational.whatIsSurebet.example.result')}
                      </span>
                      <span className="educational-example__result-value">+25 {currency}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="educational-section__scenarios">
                <h3 className="educational-section__example-title">
                  {t('landing.educational.interactiveExamples.title')}
                </h3>
                <p className="educational-section__intro-text">
                  {t('landing.educational.interactiveExamples.intro')}
                </p>

                <div className="educational-scenarios">
                  <div className="educational-scenario">
                    <h4 className="educational-scenario__title">
                      {t('landing.educational.interactiveExamples.scenario1.title')}
                    </h4>
                    <div className="educational-scenario__content">
                      <p className="educational-scenario__description">
                        {t('landing.educational.interactiveExamples.scenario1.description')}
                      </p>
                      <div className="educational-scenario__calculation">
                        <div className="educational-scenario__calc-row">
                          <span>{t('landing.educational.interactiveExamples.scenario1.calc1')}</span>
                          <span className="educational-scenario__calc-value">100 {currency}</span>
                        </div>
                        <div className="educational-scenario__calc-row">
                          <span>{t('landing.educational.interactiveExamples.scenario1.calc2')}</span>
                          <span className="educational-scenario__calc-value">102.50 {currency}</span>
                        </div>
                        <div className="educational-scenario__calc-row educational-scenario__calc-row--result">
                          <span>{t('landing.educational.interactiveExamples.scenario1.calc3')}</span>
                          <span className="educational-scenario__calc-value educational-scenario__calc-value--profit">+2.50 {currency}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="educational-scenario">
                    <h4 className="educational-scenario__title">
                      {t('landing.educational.interactiveExamples.scenario2.title')}
                    </h4>
                    <div className="educational-scenario__content">
                      <p className="educational-scenario__description">
                        {t('landing.educational.interactiveExamples.scenario2.description')}
                      </p>
                      <div className="educational-scenario__calculation">
                        <div className="educational-scenario__calc-row">
                          <span>{t('landing.educational.interactiveExamples.scenario2.calc1')}</span>
                          <span className="educational-scenario__calc-value">500 {currency}</span>
                        </div>
                        <div className="educational-scenario__calc-row">
                          <span>{t('landing.educational.interactiveExamples.scenario2.calc2')}</span>
                          <span className="educational-scenario__calc-value">512.50 {currency}</span>
                        </div>
                        <div className="educational-scenario__calc-row educational-scenario__calc-row--result">
                          <span>{t('landing.educational.interactiveExamples.scenario2.calc3')}</span>
                          <span className="educational-scenario__calc-value educational-scenario__calc-value--profit">+12.50 {currency}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {onRegisterClick && (
                  <div className="educational-section__cta">
                    <Button
                      variant="primary"
                      size="large"
                      onClick={onRegisterClick}
                      className="educational-section__cta-button"
                    >
                      {t('landing.educational.interactiveExamples.cta')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* What is Market Anomaly Section */}
        <div id="what-is-market-anomaly" className="educational-section">
          <div className="educational-section__header">
            <h2 className="educational-section__title">
              {t('landing.educational.whatIsMarketAnomaly.title')}
            </h2>
            <button
              className={`educational-section__toggle ${isExpanded('whatIsMarketAnomaly') ? 'expanded' : ''}`}
              onClick={() => toggleSection('whatIsMarketAnomaly')}
              aria-expanded={isExpanded('whatIsMarketAnomaly')}
              aria-label={isExpanded('whatIsMarketAnomaly') ? t('landing.educational.collapse') : t('landing.educational.expand')}
            >
              <svg
                className="educational-section__toggle-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {isExpanded('whatIsMarketAnomaly') && (
            <div className="educational-section__content">
              <div className="educational-section__intro">
                <p className="educational-section__intro-text">
                  {t('landing.educational.whatIsMarketAnomaly.intro')}
                </p>
              </div>

              <div className="educational-section__algorithm">
                <h3 className="educational-section__example-title">
                  {t('landing.educational.whatIsMarketAnomaly.algorithm.title')}
                </h3>
                <p className="educational-section__intro-text">
                  {t('landing.educational.whatIsMarketAnomaly.algorithm.scan')}
                </p>
                <p className="educational-section__intro-text">
                  {t('landing.educational.whatIsMarketAnomaly.algorithm.detection')}
                </p>
              </div>

              <div className="educational-section__example">
                <h3 className="educational-section__example-title">
                  {t('landing.educational.whatIsMarketAnomaly.example.title')}
                </h3>
                <div className="educational-example">
                  <div className="educational-scenario__calculation educational-scenario__calculation--compact">
                    <div className="educational-scenario__calc-row">
                      <span>{t('landing.educational.whatIsMarketAnomaly.example.marketAverage')}</span>
                      <span className="educational-scenario__calc-value">
                        {t('landing.educational.whatIsMarketAnomaly.example.marketAverageValue')}
                      </span>
                    </div>
                    <div className="educational-scenario__calc-row">
                      <span>{t('landing.educational.whatIsMarketAnomaly.example.operatorCoefficient')}</span>
                      <span className="educational-scenario__calc-value">
                        {t('landing.educational.whatIsMarketAnomaly.example.operatorCoefficientValue')}
                      </span>
                    </div>
                    <div className="educational-scenario__calc-row educational-scenario__calc-row--result">
                      <span>{t('landing.educational.whatIsMarketAnomaly.example.spread')}</span>
                      <span className="educational-scenario__calc-value educational-scenario__calc-value--profit">
                        {t('landing.educational.whatIsMarketAnomaly.example.spreadValue')}
                      </span>
                    </div>
                  </div>
                  <p className="educational-example__text">
                    {t('landing.educational.whatIsMarketAnomaly.example.conclusion')}
                  </p>
                </div>
              </div>

              <div className="educational-point educational-point--warning">
                <div className="educational-point__icon">⚠</div>
                <div className="educational-point__content">
                  <h3 className="educational-point__title">
                    {t('landing.educational.whatIsMarketAnomaly.warning.label')}
                  </h3>
                  <p className="educational-point__description">
                    {t('landing.educational.whatIsMarketAnomaly.warning.text')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Best Odds FAQ Section */}
        <div id="best-odds-faq" className="educational-section">
          <div className="educational-section__header">
            <h2 className="educational-section__title">
              {t('landing.educational.bestOddsFaq.title')}
            </h2>
            <button
              className={`educational-section__toggle ${isExpanded('bestOddsFaq') ? 'expanded' : ''}`}
              onClick={() => toggleSection('bestOddsFaq')}
              aria-expanded={isExpanded('bestOddsFaq')}
              aria-label={isExpanded('bestOddsFaq') ? t('landing.educational.collapse') : t('landing.educational.expand')}
            >
              <svg
                className="educational-section__toggle-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {isExpanded('bestOddsFaq') && (
            <div className="educational-section__content">
              <div className="educational-faq">
                <article className="educational-faq__item">
                  <h3 className="educational-faq__question">
                    {t('landing.educational.bestOddsFaq.q1.question')}
                  </h3>
                  <div className="educational-faq__answer">
                    <p>{t('landing.educational.bestOddsFaq.q1.answer')}</p>
                  </div>
                </article>

                <article className="educational-faq__item">
                  <h3 className="educational-faq__question">
                    {t('landing.educational.bestOddsFaq.q2.question')}
                  </h3>
                  <div className="educational-faq__answer">
                    <p>{t('landing.educational.bestOddsFaq.q2.answer')}</p>
                  </div>
                </article>
              </div>
            </div>
          )}
        </div>

        {/* Telegram Notifications FAQ Section */}
        <div id="telegram-notifications-faq" className="educational-section">
          <div className="educational-section__header">
            <h2 className="educational-section__title">
              {t('landing.educational.telegramNotifications.title')}
            </h2>
            <button
              className={`educational-section__toggle ${isExpanded('telegramNotifications') ? 'expanded' : ''}`}
              onClick={() => toggleSection('telegramNotifications')}
              aria-expanded={isExpanded('telegramNotifications')}
              aria-label={isExpanded('telegramNotifications') ? t('landing.educational.collapse') : t('landing.educational.expand')}
            >
              <svg
                className="educational-section__toggle-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {isExpanded('telegramNotifications') && (
            <div className="educational-section__content">
              <div className="educational-faq">
                <article className="educational-faq__item">
                  <h3 className="educational-faq__question">
                    {t('landing.educational.telegramNotifications.q1.question')}
                  </h3>
                  <div className="educational-faq__answer">
                    <p>{t('landing.educational.telegramNotifications.q1.intro')}</p>
                    <ol className="educational-faq__list educational-faq__list--ordered">
                      <li>{t('landing.educational.telegramNotifications.q1.step1')}</li>
                      <li>{t('landing.educational.telegramNotifications.q1.step2')}</li>
                      <li>{t('landing.educational.telegramNotifications.q1.step3')}</li>
                    </ol>
                  </div>
                </article>

                <article className="educational-faq__item">
                  <h3 className="educational-faq__question">
                    {t('landing.educational.telegramNotifications.q2.question')}
                  </h3>
                  <div className="educational-faq__answer">
                    <p>{t('landing.educational.telegramNotifications.q2.intro')}</p>
                    <ul className="educational-faq__list">
                      <li>{t('landing.educational.telegramNotifications.q2.reason1')}</li>
                      <li>{t('landing.educational.telegramNotifications.q2.reason2')}</li>
                      <li>{t('landing.educational.telegramNotifications.q2.reason3')}</li>
                      <li>{t('landing.educational.telegramNotifications.q2.reason4')}</li>
                    </ul>
                  </div>
                </article>
              </div>
            </div>
          )}
        </div>

        {/* How to Avoid Bookmaker Limitations Section */}
        <div className="educational-section">
          <div className="educational-section__header">
            <h2 className="educational-section__title">
              {t('landing.educational.bookmakerLimitations.title')}
            </h2>
            <button
              className={`educational-section__toggle ${isExpanded('bookmakerLimitations') ? 'expanded' : ''}`}
              onClick={() => toggleSection('bookmakerLimitations')}
              aria-expanded={isExpanded('bookmakerLimitations')}
              aria-label={isExpanded('bookmakerLimitations') ? t('landing.educational.collapse') : t('landing.educational.expand')}
            >
              <svg
                className="educational-section__toggle-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {isExpanded('bookmakerLimitations') && (
            <div className="educational-section__content">
              <div className="educational-section__intro">
                <p className="educational-section__intro-text">
                  {t('landing.educational.bookmakerLimitations.intro')}
                </p>
              </div>

              <div className="educational-section__points">
                <div className="educational-point educational-point--warning">
                  <div className="educational-point__icon">⚠</div>
                  <div className="educational-point__content">
                    <h3 className="educational-point__title">
                      {t('landing.educational.bookmakerLimitations.tip1.title')}
                    </h3>
                    <p className="educational-point__description">
                      {t('landing.educational.bookmakerLimitations.tip1.description')}
                    </p>
                  </div>
                </div>

                <div className="educational-point educational-point--warning">
                  <div className="educational-point__icon">⚠</div>
                  <div className="educational-point__content">
                    <h3 className="educational-point__title">
                      {t('landing.educational.bookmakerLimitations.tip2.title')}
                    </h3>
                    <p className="educational-point__description">
                      {t('landing.educational.bookmakerLimitations.tip2.description')}
                    </p>
                  </div>
                </div>

                <div className="educational-point educational-point--warning">
                  <div className="educational-point__icon">⚠</div>
                  <div className="educational-point__content">
                    <h3 className="educational-point__title">
                      {t('landing.educational.bookmakerLimitations.tip3.title')}
                    </h3>
                    <p className="educational-point__description">
                      {t('landing.educational.bookmakerLimitations.tip3.description')}
                    </p>
                  </div>
                </div>

                <div className="educational-point educational-point--success">
                  <div className="educational-point__icon">✓</div>
                  <div className="educational-point__content">
                    <h3 className="educational-point__title">
                      {t('landing.educational.bookmakerLimitations.tip4.title')}
                    </h3>
                    <p className="educational-point__description">
                      {t('landing.educational.bookmakerLimitations.tip4.description')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="educational-section__strategy">
                <h3 className="educational-section__strategy-title">
                  {t('landing.educational.bookmakerLimitations.strategy.title')}
                </h3>
                <div className="educational-strategy">
                  <div className="educational-strategy__step">
                    <div className="educational-strategy__step-number">1</div>
                    <div className="educational-strategy__step-content">
                      <h4 className="educational-strategy__step-title">
                        {t('landing.educational.bookmakerLimitations.strategy.step1.title')}
                      </h4>
                      <p className="educational-strategy__step-description">
                        {t('landing.educational.bookmakerLimitations.strategy.step1.description')}
                      </p>
                    </div>
                  </div>

                  <div className="educational-strategy__step">
                    <div className="educational-strategy__step-number">2</div>
                    <div className="educational-strategy__step-content">
                      <h4 className="educational-strategy__step-title">
                        {t('landing.educational.bookmakerLimitations.strategy.step2.title')}
                      </h4>
                      <p className="educational-strategy__step-description">
                        {t('landing.educational.bookmakerLimitations.strategy.step2.description')}
                      </p>
                    </div>
                  </div>

                  <div className="educational-strategy__step">
                    <div className="educational-strategy__step-number">3</div>
                    <div className="educational-strategy__step-content">
                      <h4 className="educational-strategy__step-title">
                        {t('landing.educational.bookmakerLimitations.strategy.step3.title')}
                      </h4>
                      <p className="educational-strategy__step-description">
                        {t('landing.educational.bookmakerLimitations.strategy.step3.description')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

      </div>
    </section>
  );
};

EducationalContent.propTypes = {
  onRegisterClick: PropTypes.func,
  className: PropTypes.string
};

export default EducationalContent;
